import { CUSTOMER_STATUS } from '@/modules/Customer/constants'
import { SERVICE_STATUS } from '@/modules/Service/constants'
import { TFunction } from 'i18next'

export interface MailerCampaign {
    mailer_campaign_id: number
    mailer_campaign_name: string
    mailer_campaign_description: string | null
    mailer_campaign_status: 'draft' | 'processing' | 'completed' | 'cancelled'
    mailer_campaign_type: string
    mailer_campaign_source: string | null
    mailer_campaign_source_type: 'internal' | 'external'
    mailer_campaign_internal_type?: MailerInternalType
    mailer_campaign_filters?: MailerCampaignFiltersInternal
    mailer_campaign_started_at: string | null
    mailer_campaign_completed_at: string | null
    mailer_template_id: number | null
    // Denormalized from joined template in list response — optional because not
    // returned on every endpoint (e.g. campaigns without a linked template)
    mailer_template_subject?: string
    mailer_campaign_referrer_id: number
    mailer_campaign_created_at: string
    mailer_campaign_updated_at: string
    // Full variable definitions for customer/receiver variables (per-receiver)
    mailer_campaign_customer_variables?: MailerTemplateVariableItem[]
    // Full shared variable definitions with values (same for all receivers)
    mailer_campaign_shared_values?: MailerCampaignSharedVariable[]
    metadata: MailerCampaignMetadata | null
}

// Internal source types
export type MailerInternalType = 'customers' | 'services'

// Dynamic filters for internal source
export interface MailerCampaignFiltersInternal {
    q?: string
    customer_status?: string[]
    customer_industry?: string[]
    product_group_name?: string[]
    service_status?: string[]
    service_interestings?: string[]
    customer_sale_followers?: number[]
    excluded_customer_ids?: number[]
    required_fields?: RequiredFieldType[]
    is_vn?: boolean
}

// Required fields - customers missing these fields will be excluded
export type RequiredFieldType = 'name' | 'phone' | 'email' | 'industry'

export const REQUIRED_FIELD_OPTIONS: {
    value: RequiredFieldType
    labelKey: string
}[] = [
    { value: 'name', labelKey: 'customer_name' },
    { value: 'phone', labelKey: 'phone' },
    { value: 'email', labelKey: 'email' },
    { value: 'industry', labelKey: 'customer_industry' },
]

export interface MailerCampaignMetadata {
    mailer?: {
        address: string
        user_name: string
        password: string
    }
    sender?: {
        name: string
        gender: string
        phone: string
        position: string
    }
    webhook?: {
        url: string
        events?: ('batch_update' | 'mailer_update' | 'campaign_completed')[]
    }
    /** Whether the template content uses preserved layout (full HTML document) */
    disable_layout?: boolean
}

// Variable trong template
export interface MailerTemplateVariableItem {
    name: string // auto-generated key: "ten_khach_hang", "email_cong_ty"
    label: string // user input: "Tên khách hàng", "Email công ty"
    description?: string // optional description
}

// Shared variable with value (for campaign)
export interface MailerCampaignSharedVariable
    extends MailerTemplateVariableItem {
    value?: string // the actual value for this shared variable
}

export interface MailerAttachment {
    attachment_file_url: string
    attachment_file_name: string
    attachment_file_size?: number
    attachment_file_type?: string
}

export interface MailerTemplate {
    mailer_template_id: number
    mailer_template_subject: string
    mailer_template_content: string
    mailer_template_type: string
    mailer_template_variables?: MailerTemplateVariableItem[] // Variables của template này
    mailer_template_attachments?: MailerAttachment[]
    mailer_template_created_at: string
    mailer_template_updated_at: string
}

export interface MailerReceiver {
    email: string
    name?: string
    company?: string
    phone?: string
    // Support dynamic fields from import file
    [key: string]: string | undefined
}

export interface Mailer {
    mailer_id: number
    mailer_sender: string
    mailer_receiver: string[]
    mailer_status: 'pending' | 'sent' | 'failed'
    mailer_send_at?: string | null
    mailer_performed_at: string | null
    metadata: {
        customer_name?: string
        customer_company_name?: string
        receiver_data?: Record<string, string>
        batch_id?: string
    }
}

export interface MailerBatch {
    batch_id: string
    started_at: string
    stats: {
        total: number
        pending: number
        sent: number
        failed: number
    }
    mailers: Mailer[]
}

export interface MailerBatchListResponse {
    results: MailerBatch[]
}

// Payload types
export interface MailerCampaignPayload {
    mailer_campaign_name: string
    mailer_campaign_description?: string
    mailer_campaign_type?: string
    mailer_campaign_source_type?: 'internal' | 'external'
    mailer_campaign_source?: string
    mailer_campaign_internal_type?: MailerInternalType
    mailer_campaign_filters?: MailerCampaignFiltersInternal
    mailer_template_id?: number
    // Full variable definitions for customer/receiver variables (per-receiver)
    mailer_campaign_customer_variables?: MailerTemplateVariableItem[]
    // Full shared variable definitions with values (same for all receivers)
    mailer_campaign_shared_values?: MailerCampaignSharedVariable[]
    metadata?: MailerCampaignMetadata
}

export interface UpdateMailerCampaignPayload {
    campaign: MailerCampaignPayload
    receivers?: MailerReceiver[]
    template?: {
        mailer_template_id: number
        mailer_template_content: string
        mailer_template_subject: string
        mailer_template_attachments?: MailerAttachment[]
    }
    re_upload?: boolean
}

export interface SendTestPayload {
    receiver: string
    employee_id: number
    metadata?: Record<string, unknown>
}

export interface BulkDeliveryPayload {
    metadata?: Record<string, unknown>
}

// Filter types
export interface MailerCampaignFilters {
    page?: number
    page_size?: number
    search?: string
    status?: string
    type?: string
    referrer_id?: number
}

// Response types
export interface MailerCampaignListResponse {
    campaigns: MailerCampaign[]
    pagination: {
        total: number
        page: number
        page_size: number
    }
}

export interface MailerCampaignDetailResponse {
    campaign: MailerCampaign
    template?: MailerTemplate
}

export interface MailerTemplateFilters {
    page?: number
    page_size?: number
    search?: string
}

export interface MailerTemplateListResponse {
    templates: MailerTemplate[]
    pagination: {
        total: number
        page: number
        page_size: number
    }
}

export interface CreateMailerTemplatePayload {
    mailer_template_subject: string
    mailer_template_content: string
    mailer_template_type?: string
    mailer_template_variables?: MailerTemplateVariableItem[]
}

export interface UpdateMailerTemplatePayload {
    mailer_template_subject?: string
    mailer_template_content?: string
    mailer_template_type?: string
    mailer_template_variables?: MailerTemplateVariableItem[]
}

export interface MailerReceiverListResponse {
    receivers: MailerReceiver[]
}

export interface MailerListResponse {
    mailers: Mailer[]
}

// Constants
export const MAILER_CAMPAIGN_STATUSES = [
    { value: 'draft', label: 'Draft' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
] as const

export const MAILER_SOURCE_TYPES = [
    { value: 'internal', label: 'Internal' },
    { value: 'external', label: 'External (Import)' },
] as const

// Internal source modes
export const MAILER_INTERNAL_MODES = [
    { value: 'preset', label: 'Preset Source' },
    { value: 'filters', label: 'Custom Filters' },
] as const

export type MailerInternalMode = 'preset' | 'filters'

// Internal types
export const MAILER_INTERNAL_TYPES = [
    { value: 'customers', label: 'Customers' },
] as const

// Customer status options
export const CUSTOMER_STATUS_OPTIONS = (t: TFunction) =>
    Object.values(CUSTOMER_STATUS)
        ?.filter((status) => status !== CUSTOMER_STATUS.POC)
        .map((status) => ({
            value: status,
            label: t(`status.${status.toLowerCase()}`),
        }))

// Service status options
export const SERVICE_STATUS_OPTIONS = SERVICE_STATUS

// Predefined variable schemas for internal sources
// Field names match API response: email, name, company, phone
export const INTERNAL_CUSTOMER_VARIABLES: MailerTemplateVariableItem[] = [
    { name: 'name', label: 'Tên khách hàng' },
    {
        name: 'company',
        label: 'Tên công ty',
        description: 'Nếu là khách hàng công ty',
    },
    { name: 'phone', label: 'Số điện thoại', description: 'Nếu có' },
]

export const INTERNAL_SERVICE_VARIABLES: MailerTemplateVariableItem[] = [
    { name: 'name', label: 'Tên khách hàng' },
    {
        name: 'company',
        label: 'Tên công ty',
        description: 'Nếu là khách hàng công ty',
    },
    { name: 'phone', label: 'Số điện thoại', description: 'Nếu có' },
]

// System variables - available for all campaigns (replaced by backend at send time)
export const SYSTEM_VARIABLES: MailerTemplateVariableItem[] = [
    { name: 'SEND_DATE', label: 'Ngày gửi', description: 'VD: 04/12/2025' },
    { name: 'SEND_TIME', label: 'Giờ gửi', description: 'VD: 15:45' },
    {
        name: 'SEND_DATETIME',
        label: 'Ngày giờ đầy đủ',
        description: 'VD: 04/12/2025 15:45',
    },
    {
        name: 'SEND_DAY',
        label: 'Thứ (tiếng Anh)',
        description: 'VD: Wednesday',
    },
    {
        name: 'SEND_MONTH',
        label: 'Tháng (tiếng Anh)',
        description: 'VD: December',
    },
    { name: 'SEND_YEAR', label: 'Năm', description: 'VD: 2025' },
]

// Get available variables by internal type
export const getInternalVariablesByType = (
    internalType: MailerInternalType
): MailerTemplateVariableItem[] => {
    switch (internalType) {
        case 'customers':
            return INTERNAL_CUSTOMER_VARIABLES
        case 'services':
            return INTERNAL_SERVICE_VARIABLES
        default:
            return INTERNAL_CUSTOMER_VARIABLES
    }
}

export type MailerTemplateDetailResponse = MailerTemplate

// Internal Sources
export interface MailerInternalSource {
    mailer_internal_source_id: number
    mailer_internal_source_name: string
    mailer_internal_source_description: string | null
    mailer_internal_source_query: string
    mailer_internal_source_status: 'active' | 'inactive'
    preview_count?: number
    metadata?: Record<string, unknown>
}

export interface MailerInternalSourcePreview {
    id: number
    name: string
    total: number
    receivers: MailerReceiver[]
}
